import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getAccount } from '../services/api';

/**
 * The user's profile: expense categories and budget, income sources and goal.
 *
 * Setters are exposed rather than kept private because Settings writes
 * optimistically and rolls back on failure, which needs direct control of local
 * state. See the comment on commitCategories in pages/Settings.js.
 *
 * @returns {{
 *   categories: string[], setCategories: Function,
 *   budget: number|null, setBudget: Function,
 *   incomeCategories: string[], setIncomeCategories: Function,
 *   incomeGoal: number|null, setIncomeGoal: Function,
 *   error: string
 * }}
 */
export const useAccount = () => {
  const { user } = useAuth();
  // See the note in useMonthlyEntries: keyed on the id so an auth token refresh
  // doesn't trigger a refetch.
  const userId = user?.id;
  const [categories, setCategories] = useState([]);
  const [budget, setBudget] = useState(null);
  const [incomeCategories, setIncomeCategories] = useState([]);
  const [incomeGoal, setIncomeGoal] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return undefined;
    let cancelled = false;

    getAccount()
      .then((account) => {
        if (cancelled) return;
        setCategories(account.categories ?? []);
        setBudget(account.budget.monthlyTotal);
        setIncomeCategories(account.incomeCategories ?? []);
        setIncomeGoal(account.incomeGoal);
        setError('');
      })
      .catch((caught) => {
        if (cancelled) return;
        console.error(caught);
        setError("Couldn't load your budget and categories.");
      });

    return () => {
      cancelled = true;
    };
  }, [userId]);

  return {
    categories,
    setCategories,
    budget,
    setBudget,
    incomeCategories,
    setIncomeCategories,
    incomeGoal,
    setIncomeGoal,
    error,
  };
};

export default useAccount;
